import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import { fetchSeenArtistIds } from '../attendedUtils';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

const NUMERIC_COLS = new Set(['total_shows']);
const DATE_COLS    = new Set(['last_show_date']);

// Shared between the desktop table headers and the mobile "Sort by" select,
// so both stay in sync automatically if columns ever change.
const SORT_COLUMNS = [
    { col: 'name',           label: 'Artist Name' },
    { col: 'city',           label: 'City' },
    { col: 'state',          label: 'State' },
    { col: 'country',        label: 'Country' },
    { col: 'artist_type',    label: 'Type' },
    { col: 'artist_style',   label: 'Style' },
    { col: 'total_shows',    label: 'Shows' },
    { col: 'last_show_date', label: 'Most Recent Show' },
];

function formatShowDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    });
}

// ── Mobile card (replaces the table below 768px — see
// SHARC_Mobile_Responsive_Spec_v1.1, §5.3) ──────────────────────────────────
function ArtistCardMobile({ artist }) {
    const location = [artist.city, artist.state, artist.country].filter(Boolean).join(', ');
    return (
        <Link to={`/artists/${artist.slug || artist.id}`} style={mobileStyles.card}>
            <div style={mobileStyles.cardName}>{artist.name}</div>
            {location && <div style={mobileStyles.cardLocation}>{location}</div>}
            {(artist.artist_type || artist.artist_style) && (
                <div style={mobileStyles.cardTags}>
                    {artist.artist_type && <span style={mobileStyles.tag}>{artist.artist_type}</span>}
                    {artist.artist_style && <span style={mobileStyles.tag}>{artist.artist_style}</span>}
                </div>
            )}
            <div style={mobileStyles.cardStatsRow}>
                <span>{Number(artist.total_shows) || 0} shows</span>
                <span>{formatShowDate(artist.last_show_date)}</span>
            </div>
        </Link>
    );
}

function ArtistList() {
    const isMobile = useIsMobile();
    const [artists, setArtists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // Picklists for code→name lookup in filter dropdowns
    const [picklists, setPicklists] = useState(null);

    // Filter state
    const [search, setSearch]               = useState('');
    const [filterSeen, setFilterSeen]       = useState('');
    const [filterType, setFilterType]       = useState('');
    const [filterStyle, setFilterStyle]     = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterState, setFilterState]     = useState('');

    // Seen/Not Seen data (Artist "Seen" Status, 2026-09-06) — best-effort,
    // authenticated bulk fetch. Left empty/false on any failure (including
    // simply not being logged in), same non-blocking pattern used elsewhere
    // for optional authenticated calls (e.g. ArtistDetail.js's loadAttendedStatus).
    const [seenArtistIds, setSeenArtistIds] = useState(new Set());
    const [isLoggedIn, setIsLoggedIn]       = useState(false);

    // Sort state
    const [sortCol, setSortCol] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage]       = useState(1);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo]     = useState('');
    const PAGE_SIZE = 25;

    useEffect(() => {
        document.title = 'SHARC - Artists';
        Promise.all([
            axios.get(`${config.API_URL}/artists`),
            axios.get(`${config.API_URL}/picklists`)
        ])
            .then(([artistsRes, picklistsRes]) => {
                setArtists(artistsRes.data);
                setPicklists(picklistsRes.data);
                setLoading(false);
            })
            .catch(() => { setError('Failed to load artists'); setLoading(false); });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    // Best-effort — silently leaves seenArtistIds empty / isLoggedIn false for
    // logged-out visitors or any fetch failure (Section 4.2 of the spec).
    useEffect(() => {
        fetchSeenArtistIds()
            .then(ids => { setSeenArtistIds(new Set(ids)); setIsLoggedIn(true); })
            .catch(() => { /* not logged in, or fetch failed — leave defaults */ });
    }, []);

    // code→name lookup maps built from picklists
    const countryNameMap = useMemo(() => {
        if (!picklists) return {};
        return Object.fromEntries(picklists.countries.map(c => [c.code, c.name]));
    }, [picklists]);

    // Key: "countryId|stateCode" — avoids collisions where two countries share a code (e.g. CO)
    const stateNameMap = useMemo(() => {
        if (!picklists) return {};
        return Object.fromEntries(picklists.states_regions.map(s => [`${s.country_id}|${s.code}`, s.name]));
    }, [picklists]);

    // Resolve the picklist country_id for the currently selected country code
    const selectedCountryPicklistId = useMemo(() => {
        if (!picklists || !filterCountry) return null;
        const match = picklists.countries.find(c => c.code === filterCountry);
        return match ? match.id : null;
    }, [picklists, filterCountry]);

    // Build unique filter options from data (still using codes as values)
    const artistTypes  = useMemo(() => [...new Set(artists.map(a => a.artist_type).filter(Boolean))].sort(), [artists]);
    const artistStyles = useMemo(() => [...new Set(artists.map(a => a.artist_style).filter(Boolean))].sort(), [artists]);
    const countries    = useMemo(() => {
        const unique = [...new Set(artists.map(a => a.country).filter(Boolean))];
        return unique.sort((a, b) => (countryNameMap[a] || a).localeCompare(countryNameMap[b] || b));
    }, [artists, countryNameMap]);
    const states       = useMemo(() => {
        const src = filterCountry
            ? artists.filter(a => a.country === filterCountry)
            : artists;
        return [...new Set(src.map(a => a.state).filter(Boolean))].sort();
    }, [artists, filterCountry]);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    };

    const filtered = useMemo(() => {
        const q = normalizeStr(search);
        return artists.filter(a => {
            if (search && !((a.name && normalizeStr(a.name).includes(q)) || (a.city && normalizeStr(a.city).includes(q)))) return false;
            // Logged-out + Seen or Not Seen selected → zero results (explicit
            // check rather than relying on an empty seenArtistIds set, which
            // would make "Not Seen" incorrectly match every artist).
            if (filterSeen && !isLoggedIn) return false;
            if (filterSeen === 'seen'     && !seenArtistIds.has(a.id)) return false;
            if (filterSeen === 'not_seen' &&  seenArtistIds.has(a.id)) return false;
            if (filterType    && a.artist_type !== filterType)    return false;
            if (filterStyle   && a.artist_style !== filterStyle)  return false;
            if (filterCountry && a.country !== filterCountry)     return false;
            if (filterState   && a.state !== filterState)         return false;
            if (dateFrom && (!a.last_show_date || a.last_show_date.slice(0,10) < dateFrom)) return false;
            if (dateTo   && (!a.last_show_date || a.last_show_date.slice(0,10) > dateTo))   return false;
            return true;
        });
    }, [artists, search, filterSeen, isLoggedIn, seenArtistIds, filterType, filterStyle, filterCountry, filterState, dateFrom, dateTo]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            const av = NUMERIC_COLS.has(sortCol) ? Number(a[sortCol] ?? 0) : (a[sortCol] ?? '');
            const bv = NUMERIC_COLS.has(sortCol) ? Number(b[sortCol] ?? 0) : (b[sortCol] ?? '');
            const cmp = NUMERIC_COLS.has(sortCol)
                ? av - bv
                : DATE_COLS.has(sortCol)
                    ? (av || '').localeCompare(bv || '')
                    : String(av).localeCompare(String(bv));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [filtered, sortCol, sortDir]);

    const clearFilters = () => {
        setSearch(''); setFilterSeen(''); setFilterType(''); setFilterStyle('');
        setFilterCountry(''); setFilterState('');
        setDateFrom(''); setDateTo('');
        setPage(1);
    };

    const hasFilters = search || filterSeen || filterType || filterStyle || filterCountry || filterState || dateFrom || dateTo;

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span style={S.sortIcon}>↕</span>;
        return <span style={{ ...S.sortIcon, color: '#bb86fc' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    if (loading) return <div style={S.message}>Loading artists...</div>;
    if (error)   return <div style={S.errMsg}>{error}</div>;

    const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            {/* ── Header ── */}
            <div style={isMobile ? { ...S.header, ...Smobile.header } : S.header}>
                <h1 style={S.title}>Artists</h1>
                <div style={S.headerRight}>
                    <span style={S.count}>{sorted.length.toLocaleString()} of {artists.length.toLocaleString()}</span>
                    <Link to="/add-artist" style={S.addButton}>+ Add Artist</Link>
                </div>
            </div>

            {/* ── Filters Row 1: text search + date range ── */}
            <div style={S.filterBar}>
                <input
                    type="text"
                    placeholder="Search by artist name or city..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    style={S.searchInput}
                />
                <span style={S.dateLabel}>Most Recent Show</span>
                <input
                    type="date"
                    value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                    style={S.dateInput}
                />
                <span style={S.capDash}>–</span>
                <input
                    type="date"
                    value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setPage(1); }}
                    style={S.dateInput}
                />
                {hasFilters && (
                    <button onClick={clearFilters} style={S.clearBtn}>✕ Clear all</button>
                )}
            </div>

            {/* ── Filters Row 2: dropdowns ── */}
            <div style={S.filterBar}>
                <select value={filterSeen} onChange={e => { setFilterSeen(e.target.value); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">Seen or Not Seen</option>
                    <option value="seen">Seen</option>
                    <option value="not_seen">Not Seen</option>
                </select>
                <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Types</option>
                    {artistTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={filterStyle} onChange={e => { setFilterStyle(e.target.value); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Styles</option>
                    {artistStyles.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setFilterState(''); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Countries</option>
                    {countries.map(c => (
                        <option key={c} value={c}>{countryNameMap[c] || c}</option>
                    ))}
                </select>
                <select value={filterState} onChange={e => { setFilterState(e.target.value); setPage(1); }} style={{ ...S.filterSelect, flex: '1 1 140px' }} disabled={states.length === 0}>
                    <option value="">All States</option>
                    {states.map(s => (
                        <option key={s} value={s}>{stateNameMap[`${selectedCountryPicklistId}|${s}`] || s}</option>
                    ))}
                </select>
            </div>

            {/* ── Mobile-only sort control (table headers below handle this on desktop) ── */}
            {isMobile && (
                <div style={mobileStyles.sortRow}>
                    <select
                        value={sortCol}
                        onChange={e => { setSortCol(e.target.value); setPage(1); }}
                        style={mobileStyles.sortSelect}
                    >
                        {SORT_COLUMNS.map(({ col, label }) => (
                            <option key={col} value={col}>Sort: {label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                        style={mobileStyles.sortDirBtn}
                        aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
                    </button>
                </div>
            )}

            {/* ── Table ── */}
            {sorted.length > PAGE_SIZE && (
                <div style={S.pagination}>
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        style={{ ...S.pageBtn, ...(page === 1 ? S.pageBtnDisabled : {}) }}
                    >← Prev</button>
                    <div style={S.pageNumbers}>
                        {Array.from({ length: Math.ceil(sorted.length / PAGE_SIZE) }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === Math.ceil(sorted.length / PAGE_SIZE) || Math.abs(p - page) <= 2)
                            .reduce((acc, p, i, arr) => {
                                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                                acc.push(p);
                                return acc;
                            }, [])
                            .map((p, i) => p === '...'
                                ? <span key={`ellipsis-${i}`} style={S.ellipsis}>…</span>
                                : <button key={p} onClick={() => setPage(p)} style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }}>{p}</button>
                            )
                        }
                    </div>
                    <button
                        onClick={() => setPage(p => Math.min(Math.ceil(sorted.length / PAGE_SIZE), p + 1))}
                        disabled={page === Math.ceil(sorted.length / PAGE_SIZE)}
                        style={{ ...S.pageBtn, ...(page === Math.ceil(sorted.length / PAGE_SIZE) ? S.pageBtnDisabled : {}) }}
                    >Next →</button>
                    <span style={S.pageInfo}>
                        {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length.toLocaleString()}
                    </span>
                </div>
            )}

            {sorted.length === 0 ? (
                <div style={S.message}>
                    {hasFilters ? 'No artists match your filters.' : 'No artists yet.'}
                </div>
            ) : isMobile ? (
                <div style={mobileStyles.cardList}>
                    {pageItems.map((a) => (
                        <ArtistCardMobile key={a.id} artist={a} />
                    ))}
                </div>
            ) : (
                <div style={S.tableContainer}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                {SORT_COLUMNS.map(({ col, label }) => (
                                    <th
                                        key={col}
                                        style={{ ...S.th, cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort(col)}
                                    >
                                        {label} <SortIcon col={col} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((a, idx) => (
                                <tr
                                    key={a.id}
                                    style={idx % 2 === 0 ? S.trEven : S.trOdd}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#252525'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#1e1e1e' : '#1a1a1a'}
                                >
                                    <td style={S.td}>
                                        <Link to={`/artists/${a.slug || a.id}`} style={S.link}>{a.name}</Link>
                                    </td>
                                    <td style={S.td}>{a.city || '—'}</td>
                                    <td style={S.td}>{a.state || '—'}</td>
                                    <td style={S.td}>{a.country || '—'}</td>
                                    <td style={S.td}>{a.artist_type || '—'}</td>
                                    <td style={S.td}>{a.artist_style || '—'}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{Number(a.total_shows) || 0}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{formatShowDate(a.last_show_date)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const S = {
    header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:       { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
    count:       { fontSize: '13px', color: '#666' },
    addButton:   { backgroundColor: '#bb86fc', color: '#121212', textDecoration: 'none', padding: '8px 18px', borderRadius: '4px', fontWeight: 'bold', fontSize: '14px' },

    filterBar:    { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' },
    searchInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', flex: '1 1 260px', minWidth: '200px', boxSizing: 'border-box' },
    filterSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', color: '#e0e0e0', flex: '0 1 140px', minWidth: '110px', boxSizing: 'border-box' },
    clearBtn:     { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

    tableContainer: { overflowX: 'auto' },
    table:          { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th:             { textAlign: 'left', padding: '10px 14px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td:             { padding: '10px 14px', color: '#e0e0e0', borderBottom: '1px solid #222', whiteSpace: 'nowrap' },
    trEven:         { backgroundColor: '#1e1e1e' },
    trOdd:          { backgroundColor: '#1a1a1a' },
    link:           { color: '#bb86fc', textDecoration: 'none' },
    numCell:        { textAlign: 'right', color: '#aaa', fontVariantNumeric: 'tabular-nums' },
    dateLabel:  { fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
    dateInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', color: '#e0e0e0', width: '130px', boxSizing: 'border-box' },
    capDash:    { color: '#555', fontSize: '14px' },
    sortIcon:   { fontSize: '10px', marginLeft: '4px', color: '#555' },
    message:        { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errMsg:         { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
    pagination:     { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' },
    pageNumbers:    { display: 'flex', gap: '4px', alignItems: 'center' },
    pageBtn:        { backgroundColor: '#2c2c2c', color: '#e0e0e0', border: '1px solid #444', borderRadius: '4px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer', minWidth: '36px', textAlign: 'center' },
    pageBtnActive:  { backgroundColor: '#bb86fc', color: '#121212', border: '1px solid #bb86fc', fontWeight: 700 },
    pageBtnDisabled:{ opacity: 0.35, cursor: 'default' },
    ellipsis:       { color: '#555', padding: '0 4px', fontSize: '13px' },
    pageInfo:       { fontSize: '12px', color: '#666', marginLeft: '8px' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_v1.1, §4.3/§5.3) ──
const Smobile = {
    // "Artists" heading + count/+Add group can overflow a nowrap flex row on
    // narrow viewports — allow it to wrap onto a second line instead.
    header: {
        flexWrap: 'wrap',
        gap: '10px',
    },
};

// Kept as a separate object, only used when useIsMobile() is true, so the
// desktop table above is completely untouched.
const mobileStyles = {
    sortRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
    },
    sortSelect: {
        flex: '1 1 auto',
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 12px',
        fontSize: '13px',
        color: '#e0e0e0',
        boxSizing: 'border-box',
    },
    sortDirBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 14px',
        fontSize: '13px',
        color: '#e0e0e0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    card: {
        display: 'block',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '14px 16px',
        textDecoration: 'none',
        color: '#e0e0e0',
    },
    cardName: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#bb86fc',
        marginBottom: '4px',
    },
    cardLocation: {
        fontSize: '13px',
        color: '#ccc',
        marginBottom: '8px',
    },
    cardTags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '8px',
    },
    tag: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '2px 8px',
        fontSize: '12px',
        color: '#bb86fc',
        fontWeight: 'bold',
    },
    cardStatsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '12px',
        color: '#aaa',
        paddingTop: '8px',
        borderTop: '1px solid #2a2a2a',
    },
};

export default ArtistList;
